/** @jsxImportSource @emotion/react */
import React from 'react'
import { Button, Modal } from 'react-bootstrap'
import ReactSelect from 'react-select'
import Input from 'components/atoms/Input'
import Toast from 'components/atoms/Toast'
import { useGetAllBillingProceduresListQuery } from 'store/services/billing-procedures'
import { useCreateBillingChargeMutation } from 'store/services/billingCharges'
import { CreateBillingChargeDto } from 'store/types'

type Props = {
  open: boolean
  onClose: () => void
  patientId: string | number
}

export const QuickChargeModal: React.FC<Props> = ({ open, onClose, patientId }) => {
  const { data: procedures } = useGetAllBillingProceduresListQuery({})
  const [createCharge, { isLoading }] = useCreateBillingChargeMutation()
  const [cpt, setCpt] = React.useState<string>('')
  const [amount, setAmount] = React.useState<string>('')

  const options = React.useMemo(
    () =>
      (procedures || []).map((p) => ({ value: p.cptCode, label: `${p.cptCode} - ${p.name}` })),
    [procedures],
  )

  const onSubmit = async () => {
    const num = Number(amount)
    if (!cpt || !num || num <= 0) {
      return
    }

    const today = new Date().toISOString()

    const payload: CreateBillingChargeDto = {
      patientId: String(patientId),
      placeOfService: '11',
      locationCode: '3', // TestSmarter - FL
      claimType: 'ORIGINAL',
      renderingAndBilling: 'TEST_SMARTER',
      claimSubmissionMethod: null,
      type: 'QUICK',
      diagnosisCodes: [],
      procedures: [
        {
          cptCode: cpt,
          startDos: today,
          endDos: today,
          modifier: '1',
          dxPointers: '1',
          units: 'UN',
          unitsValue: 1,
          patientAmount: num,
          planAmount: 0,
        },
      ],
      totalPatientAmount: num,
      totalPlanAmount: 0,
    }

    try {
      await createCharge(payload).unwrap()
      Toast({ type: 'success', label: 'Charge created!' })
      onClose()
    } catch (e) {
      const message = (e as { data?: { message?: string } })?.data?.message
      Toast({ type: 'error', label: message || 'Failed to create charge' })
    }
  }

  return (
    <Modal
      show={open}
      onHide={onClose}
      css={{
        '.modal-content': { borderRadius: 15 },
        '.modal-body': { padding: '20px' },
      }}
      centered>
      <div css={{ textAlign: 'center', padding: '20px 0', fontWeight: 600, fontSize: 22 }}>
        Quick Charge
      </div>
      <Modal.Body>
        <div className="row">
          <div className="col-8">
            <label className="textGray">CPT Code</label>
            <ReactSelect
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 1051 }) }}
              className="basic-single"
              classNamePrefix="select"
              isClearable
              isSearchable
              placeholder="Select CPT"
              name="cptCode"
              options={options}
              onChange={(item) => {
                setCpt(item ? String(item.value) : '')

                const procedure = procedures?.find((p) => p.cptCode === item?.value)

                if (procedure?.charge) {
                  setAmount(String(procedure.charge))
                }
              }}
            />
          </div>
          <div className="col-4">
            <label className="textGray">Amount</label>
            <Input
              value={amount}
              name="amount"
              placeholder="$0.00"
              type="number"
              step="0.01"
              minValue={'0'}
              handleChange={(e) => setAmount(e.target.value)}
              className="form-control"
              parentDiv={null}
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-end">
        <Button variant="outline-secondary" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button className="ml-2" variant="primary" onClick={onSubmit} disabled={isLoading}>
          Submit
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
